import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  type Unsubscribe
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { useUserStore } from './userStore'
import { useAppState } from '@/composables/useAppState'

export type AiJobStatus = 'pending' | 'processing' | 'done' | 'failed'

export interface AiJob {
  id: string
  userId: string
  type:
  | 'generate_recipe_image'
  | 'generate_step_image'
  | 'enhance_image'
  | 'generate_recipe'
  | 'generate_description'
  | 'scan_recipe'
  | 'generate_recipe_book_cover'
  | 'scan_pantry'
  | 'scan_receipt'
  status: AiJobStatus
  result?: {
    imageUrl?: string
    imageRef?: string
    recipe?: any
    description?: string
    items?: Array<Record<string, any>>
    store?: string
    date?: string
    total?: number
  }
  error?: string
  params?: Record<string, any>
  created_at?: any
  completed_at?: any
  reviewed?: boolean // Local flag for UI filtering
}

export const useAiJobStore = defineStore('aiJob', () => {
  const userStore = useUserStore()
  const { showSnackbar } = useAppState()
  const jobs = ref<AiJob[]>([])
  const loading = ref(false)
  let unsubscribe: Unsubscribe | null = null
  let boundUserId: string | null = null

  function requireAuthenticatedUser(): string {
    const userId = userStore.firebaseUser?.uid
    if (!userId) {
      throw new Error('Sign in to access AI jobs.')
    }
    return userId
  }

  // Getters
  const pendingJobs = computed(() => jobs.value.filter(j => j.status === 'pending' || j.status === 'processing'))
  const completedJobs = computed(() => jobs.value.filter(j => (j.status === 'done' || j.status === 'failed') && !j.reviewed))
  const allJobs = computed(() => jobs.value)

  const pendingCount = computed(() => pendingJobs.value.length)
  const completedCount = computed(() => completedJobs.value.length)

  // Actions
  function bindJobs() {
    const userId = userStore.firebaseUser?.uid
    if (!userId) {
      unbindJobs()
      return
    }
    if (unsubscribe && boundUserId === userId) return
    unbindJobs()

    loading.value = true
    boundUserId = userId
    const db = getFirestore()
    const q = query(
      collection(db, 'ai_image_jobs'),
      where('userId', '==', userId),
      orderBy('created_at', 'desc')
    )

    unsubscribe = onSnapshot(q, (snapshot) => {
      if (userStore.firebaseUser?.uid !== userId) return
      jobs.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AiJob[]
      loading.value = false
    }, (err) => {
      if (userStore.firebaseUser?.uid !== userId) return
      console.error('Error binding AI jobs:', err)
      jobs.value = []
      loading.value = false
      unsubscribe = null
      boundUserId = null
    })
  }

  function unbindJobs() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    boundUserId = null
    jobs.value = []
    loading.value = false
  }

  async function enqueueJob(type: string, params: any) {
    requireAuthenticatedUser()
    const functions = getFunctions()
    const enqueueFn = httpsCallable(functions, 'enqueueAIImageJob')
    await enqueueFn({ type, ...params })
  }

  function enqueueJobInBackground(type: string, params: any): void {
    void enqueueJob(type, params).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to queue AI request'
      showSnackbar(`AI request could not be queued: ${message}`, 6000)
    })
  }

  async function markAsReviewed(jobId: string) {
    requireAuthenticatedUser()
    const db = getFirestore()
    const jobRef = doc(db, 'ai_image_jobs', jobId)
    await updateDoc(jobRef, { reviewed: true })
  }

  async function deleteJob(jobId: string) {
    requireAuthenticatedUser()
    const db = getFirestore()
    const jobRef = doc(db, 'ai_image_jobs', jobId)
    await deleteDoc(jobRef)
  }

  watch(
    () => userStore.firebaseUser?.uid ?? null,
    (userId) => {
      if (userId) {
        bindJobs()
      } else {
        unbindJobs()
      }
    },
    { immediate: true }
  )

  return {
    jobs,
    loading,
    pendingJobs,
    completedJobs,
    allJobs,
    pendingCount,
    completedCount,
    bindJobs,
    unbindJobs,
    enqueueJob,
    enqueueJobInBackground,
    markAsReviewed,
    deleteJob
  }
})
